import React, { useRef, useState } from 'react';
import styles from './HATVApprovalActionModal.module.scss';
import { useTranslation } from 'react-i18next';
import dropdownArrowLight from '../../../assets/images/LightTheme/down_arrow_light.svg';
import dropdownArrowDark from '../../../assets/images/DarkTheme/down_arrow_dark.svg';
import { useAppSelector } from '../../../redux/hooks';
import { HATV_APPROVAL_ACTION } from '../../../constants/dataConstants';
import FormatterBar from '../../common/FormatterBar/FormaterBarWithLinkmodel/FormatterBar';
import Button from '../../common/Button/Button';
import useClickOutside from '../../CustomHooks/useClickOutside';
import { useUpdateApprovalStatusMutation } from '../../../redux/services/hatvAwardApprovalAPI';
import { getFromLocalStorage, useProfileParams } from '../../../utils/common';
import { withUtag } from '../../../utils/analytics';
import { AWARD_APPROVAL_STATUS_APPROVER } from '../../../constants/dataConstants';
import i18n from '../../../i18n';

export interface IApproveModalProps {
  aboNumber: string;
  affiliateCode: string;
  aboHatvAwardApproverIdToApprove: number | null;
  aboHatvAwardApproverIdToDeny: number | null;
  approvedFor: string;
  approvalType: number;
  onClose: () => void;
  setIsStatusUpdate?: (apiStatus: string) => void;
  currentAward?: string;
  currentStatus?: number;
  currentAwardCode?: string | number | null;
  awardQualifiedMonth?: string;
}

const HATVApprovalActionModal: React.FC<IApproveModalProps> = ({
  aboNumber,
  affiliateCode,
  aboHatvAwardApproverIdToApprove,
  aboHatvAwardApproverIdToDeny,
  approvedFor,
  approvalType,
  onClose,
  setIsStatusUpdate,
  currentAward,
  currentStatus,
  currentAwardCode,
  awardQualifiedMonth,
}) => {
  const { t } = useTranslation();
  const fixedT = i18n.getFixedT('en'); // Gives always english
  const [isOpen, setIsOpen] = useState(false);
  const [selectedApprovalStatus, setSelectedApprovalType] = useState<number | null>(approvalType);
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dropdownArrow = currentTheme === 'light' ? dropdownArrowLight : dropdownArrowDark;

  const [textareaValue, setTextareaValue] = useState<string>('');
  const [isEditable, setIsEditable] = useState(false);
  const [isNoteEditable, setNoteEditable] = useState(false);
  const [saveEnable, setSaveEnable] = useState(false);

  const [updateApprovalStatus, { isLoading: isUpdating, isSuccess: isUpdated }] =
    useUpdateApprovalStatusMutation();

  const { aboNumber: urlParamAboNumber } = useProfileParams();
  const isDetailsPage = !!(urlParamAboNumber && urlParamAboNumber?.toString() === aboNumber);
  const approvalCurrentStatus = AWARD_APPROVAL_STATUS_APPROVER.find(
    (statusCode) => statusCode?.code === Number(currentStatus)
  )
    ? AWARD_APPROVAL_STATUS_APPROVER.find(
        (statusCode) => statusCode?.code === Number(currentStatus)
      )?.label
    : 'NOT_VERIFIED';
  const approvalDropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(approvalDropdownRef, () => setIsOpen(false));

  const toggleDropdown = (e: React.MouseEvent) => {
    setIsOpen((isOpen) => !isOpen);
  };

  const handleApprovalChange = (selectedCode: number) => {
    setSelectedApprovalType(selectedCode);
  };
  const textAreaChangeHandler = (editorData: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorData, 'text/html');
    const rowNote = doc.body.textContent || '';
    if (rowNote && rowNote.length > 0) {
      setSaveEnable(true);
    } else {
      setSaveEnable(false);
    }
    editorData === '<p><br></p>' ? setTextareaValue('') : setTextareaValue(editorData);
  };

  const handleClickCancel = () => {
    setIsEditable(false);
    setNoteEditable(false);
    onClose();
  };

  const handleClickSave = async () => {
    if (!selectedApprovalStatus) return;
    const aboHatvAwardApproverId =
      selectedApprovalStatus === HATV_APPROVAL_ACTION.APPROVE
        ? aboHatvAwardApproverIdToApprove
        : aboHatvAwardApproverIdToDeny;

    if (!aboHatvAwardApproverId) return;
    updateApprovalStatus({
      aboNumber,
      affiliateCode,
      payload: {
        aboHatvAwardApproverId,
        approvalStatus: selectedApprovalStatus,
        note: textareaValue,
      },
    })
      .unwrap()
      .then(() => {
        setIsStatusUpdate && setIsStatusUpdate('success');
        const currentAwardDetails = getFromLocalStorage<storedCurrentAwardDetails>('currentAward');
        const awardStatusTrackData = {
          event_name: 'award_status_change',
          abo_account: aboNumber,
          current_award: isDetailsPage
            ? currentAwardDetails?.currentAwardName || ''
            : currentAward || '', // the current award of abo
          award_status: fixedT(`HATV_APPROVAL_STATUS.${approvalCurrentStatus}`),
          to_status: selectedApprovalStatus === 3 ? 'Approve' : 'Deny', // New status of the award
          approval_type: isDetailsPage ? 'award_detail' : 'award_dashboard',
          am_country: localStorage?.getItem('userCountryCode') || '',
          am_affiliate: localStorage?.getItem('userAffiliateCode') || '',
          award_id: isDetailsPage
            ? currentAwardDetails?.currentAwardCode || ''
            : currentAwardCode || '',
          award_month_qual: awardQualifiedMonth || '',
        };

        withUtag().then((utag) => utag.link(awardStatusTrackData));
      })
      .catch(() => {
        setIsStatusUpdate && setIsStatusUpdate('fail');
      });
  };

  return (
    <div className={`approval_container ${styles.approval_container}`}>
      <div className={`${styles.modal_title}`}>{t('update_award_status')}</div>
      <div className={`${styles.approvedForSection}`}>
        {t('update_award_status_for')}
        <span className={`${styles.approvedForText}`}>{`${approvedFor}`}</span>
      </div>
      <div
        className={`${styles.input_wrapper}`}
        data-testid="approve_toggle__testId"
        ref={approvalDropdownRef}
      >
        <input
          type="text"
          className={`approval_input ${styles.dropdown_input} ${isOpen ? 'selected_input' : ''}`}
          value={selectedApprovalStatus ? t(`hatv_approval_actions.${selectedApprovalStatus}`) : ''}
          readOnly
          data-testid="approve_dropdown_input_testId"
          placeholder={t('award_status')}
        />
        <img
          src={dropdownArrow}
          alt="Downkey"
          className={`${styles.arrow_icon} ${isOpen ? styles.rotate : ''}`}
          onClick={toggleDropdown}
        />
        {isOpen && (
          <div className={`dropdown_menu ${styles.dropdown_menu}`}>
            {aboHatvAwardApproverIdToApprove && (
              <div
                className={`dropdown_item ${selectedApprovalStatus === HATV_APPROVAL_ACTION.APPROVE ? 'dropdown_selected_item' : ''} ${styles.dropdown_item}`}
                onClick={() => handleApprovalChange(HATV_APPROVAL_ACTION.APPROVE)}
              >
                {t(`hatv_approval_actions.${HATV_APPROVAL_ACTION.APPROVE}`)}
              </div>
            )}
            {aboHatvAwardApproverIdToDeny && (
              <div
                className={`dropdown_item ${selectedApprovalStatus === HATV_APPROVAL_ACTION.DENY ? 'dropdown_selected_item' : ''} ${styles.dropdown_item}`}
                onClick={() => handleApprovalChange(HATV_APPROVAL_ACTION.DENY)}
              >
                {t(`hatv_approval_actions.${HATV_APPROVAL_ACTION.DENY}`)}
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.button_input_wrapper}>
        <div className={styles.formatter_wrapper} data-testid="editor_testID">
          <FormatterBar
            id="notes"
            testId="notes_testID"
            editorData={textareaValue}
            handleDataChange={textAreaChangeHandler}
            section={'htvApproval'}
            placeholder={t('add_note_placeholder')}
            isContentEditable={isEditable}
            maxCharacter={1000}
          />
        </div>
        <div className={styles.btn_wrapper}>
          <Button
            id="cancelButton"
            onClick={handleClickCancel}
            label={t('cancel_btn')}
            style="unfilled"
            className={styles.btn_styles}
          />
          <Button
            id="saveButton"
            onClick={handleClickSave}
            label={t('update_btn')}
            style="filled"
            isDisabled={!selectedApprovalStatus || isUpdating || isUpdated}
            className={styles.btn_styles}
          />
        </div>
      </div>
    </div>
  );
};

export default HATVApprovalActionModal;
